import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiKey } from '@/injection/keys.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    Holding, HoldingCreateForm, HoldingCreateFormErrors, HoldingCreateFormErrorsSchema,
    HoldingSchema,
    HoldingsPaginator,
    HoldingsPaginatorSchema, HoldingsRequestFilter,
    HoldingsSchema, HoldingUpdateForm, HoldingUpdateFormErrors, HoldingUpdateFormErrorsSchema,
} from '@/types/holding.type.ts';
import { DealerListItem, DealersSchema } from '@/types/dealer.type.ts';

class HoldingRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getHolding(id: number): Promise<Holding>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getHolding', requestParams)
                .then(({ data }: any) => {
                    const parsedHolding = HoldingSchema.parse(data);
                    const holding: Holding = parsedHolding as unknown as Holding;

                    resolve(holding);
                });
        });
    }

    public getHoldings(holdingsRequestFilter?: HoldingsRequestFilter, oAbortController?: AbortController): Promise<HoldingsPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: holdingsRequestFilter,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getHoldings', requestParams, null, oAbortController)
                .then(({ data }: any) => {
                    const parsedHoldingsPaginator = HoldingsPaginatorSchema.parse(data);
                    const holdingsPaginator: HoldingsPaginator = parsedHoldingsPaginator as unknown as HoldingsPaginator;

                    resolve(holdingsPaginator);
                });
        });
    }

    public createHolding(holdingCreateForm: HoldingCreateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: holdingCreateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('createHolding', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedCreateHoldingErrors = HoldingCreateFormErrorsSchema.parse(errors);
                        const createHoldingsErrors: HoldingCreateFormErrors = parsedCreateHoldingErrors as unknown as HoldingCreateFormErrors;

                        reject(createHoldingsErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public updateHolding(id: number, holdingUpdateForm: HoldingUpdateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: holdingUpdateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateHolding', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedUpdateHoldingErrors = HoldingUpdateFormErrorsSchema.parse(errors);
                        const updateHoldingErrors: HoldingUpdateFormErrors = parsedUpdateHoldingErrors as unknown as HoldingUpdateFormErrors;

                        reject(updateHoldingErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public activateHolding(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('activateHolding', requestParams)
                .then(() => resolve());
        });
    }

    public deactivateHolding(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('deactivateHolding', requestParams)
                .then(() => resolve());
        });
    }

    public getAllActiveHoldings(): Promise<Holding[]>
    {
        return new Promise((resolve) => {
            this.$Api.callNew('getAllActiveHoldings')
                .then(({ data }: any) => {
                    const parsedHoldings = HoldingsSchema.parse(data);
                    const holdings: Holding[] = parsedHoldings as unknown as Holding[];

                    resolve(holdings);
                });
        });
    }

    public getHoldingDealers(id: number): Promise<DealerListItem[]>
    {
        return new Promise((resolve) => {
            const requestParams: ApiRequestParams = {
                segments: { id },
            };

            this.$Api.callNew('getHoldingDealers', requestParams)
                .then(({ data }: any) => {
                    const parsedDealers = DealersSchema.parse(data);
                    const dealers = parsedDealers as unknown as DealerListItem[];

                    resolve(dealers);
                });
        });
    }
}

export default HoldingRepository;
